import {
  ArrowDownImage,
  Flex,
  ReportSection,
  ReportSectionItemContent,
  ReportSectionItemTable,
  type ReportSectionItemTableProps,
  type ReportSectionProps,
  type ReportTableCell,
  type ReportTableProps,
  ScalingButtonGroup,
  Text,
} from '@/components';
import type { LedgerActivityFlowSectionProps } from '@/features/mold/models/ledger';
import { SPACE_TEXT } from '@/utils';
import classNames from 'classnames';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Panel, ReactFlowProvider } from 'reactflow';
import {
  LEDGER_BORDER,
  convertActivityAndResultTargetTableDataValue,
  getActivityAndResultTargetTableDataSource,
  getActivityAndResultTargetTableDataYearText,
  getIndicatorDescriptionLabel,
  getIndicatorSectionTitle,
  getIndicatorTitleLabel,
  parseTreeToNodesAndEdges,
} from '../../../utils';
import { ActivityTree } from '../../flow';
import styles from './LedgerPrint.module.scss';

const printHiddenClass = classNames(styles.hidden_print);

export const ActivityFlowSection: FC<LedgerActivityFlowSectionProps> = ({
  project,
  nestedConsecutiveIndicators,
  tree,
  selectOptions,
}) => {
  const { t } = useTranslation();

  const fiscalYear = project?.fiscalYear;
  const activityTreeData = parseTreeToNodesAndEdges(tree);

  return (
    <>
      <ReportSection
        title={t('way-to-effect')}
        containSize
        items={[
          {
            wrapperStyle: {
              borderLeft: 'none',
            },
            item: (
              <ReportSectionItemContent
                label={t('connection-of-activities-and-performance-target')}
                wrapperStyle={{
                  breakInside: 'inherit',
                  flexDirection: 'column',
                  borderLeft: 'none',
                  borderBottom: 'none',
                }}
                labelStyle={{
                  width: '100%',
                  borderLeft: 'none',
                  borderRight: 'none',
                  borderBottom: LEDGER_BORDER,
                }}
                value={
                  <ReactFlowProvider>
                    <ActivityTree
                      hideBorder
                      initialNodes={activityTreeData.nodes}
                      initialEdges={activityTreeData.edges}
                      panelNode={() => {
                        return (
                          <Panel
                            position="top-right"
                            className={printHiddenClass}
                          >
                            <ScalingButtonGroup />
                          </Panel>
                        );
                      }}
                    />
                  </ReactFlowProvider>
                }
              />
            ),
          },
        ]}
      />
      {selectOptions.map((option, idx) => {
        const isLast = idx === selectOptions.length - 1;
        return (
          <ReportSection
            // biome-ignore lint/suspicious/noArrayIndexKey: biomeをCIに仕込むために暫定的にignore。keyにユニークな値を指定するか適切な理由に書き換えること
            key={idx}
            title={`${t('expression-path-from-activity')} ${option.label}`}
            items={[
              {
                subtitle: t('activity'),
                isLongText: true,
                item: (
                  <ReportSectionItemContent
                    value={option.rootActivity.activityContent}
                    isLast
                  />
                ),
              },
              ...(tree
                ? option.routeIndicators.map((routeIndicator) => {
                    const currentEdge = tree.edges.find(
                      (edge) => edge.id === routeIndicator.path.fromEdgeId,
                    );
                    return {
                      indicator: routeIndicator.indicator,
                      edge: currentEdge,
                    };
                  })
                : []
              ).flatMap((routeIndicator, idx, arr) => {
                if (!fiscalYear) {
                  return [];
                }
                const currentConsecutiveIndicators =
                  nestedConsecutiveIndicators?.find((v) =>
                    v.some((s) => s.id === routeIndicator.indicator.id),
                  ) ?? [];
                const nextRouteIndicator: typeof routeIndicator | undefined =
                  arr[idx + 1];
                const { ledgerViewYears, tableDataSource } =
                  getActivityAndResultTargetTableDataSource({
                    data: currentConsecutiveIndicators,
                    indicatorType: routeIndicator.indicator.type,
                    indicatorEvaluationDirection:
                      routeIndicator.indicator.evaluationDirection,
                    indicatorUnit: routeIndicator.indicator.unit,
                    fiscalYear,
                    indicatorTerm: routeIndicator.indicator.term,
                  });

                const yearsByIsTarget = currentConsecutiveIndicators.flatMap(
                  (v) => (v.isTargetYear ? [v.fiscalYear] : []),
                );

                const tableHeader: ReportSectionItemTableProps['header'] = [
                  {
                    type: 'text',
                    value: SPACE_TEXT,
                    wrapperStyle: {
                      height: ledgerViewYears.length ? 'auto' : 20,
                    },
                    ratio: 10,
                  },
                  ...(ledgerViewYears.map((year) => {
                    const yearText =
                      getActivityAndResultTargetTableDataYearText({
                        year,
                        indicatorType: routeIndicator.indicator.type,
                        term: routeIndicator.indicator.term,
                        yearsByIsTarget,
                        dataNatureType: routeIndicator.indicator.dataNatureType,
                      });
                    return {
                      type: 'text',
                      value: yearText,
                      align: 'center',
                      ratio: 10,
                    };
                  }) satisfies ReportSectionItemTableProps['header']),
                ];

                const tableBody: ReportTableProps['data'] = tableDataSource.map(
                  (data) => {
                    const { label, ...yearObj } = data;
                    return {
                      row: [
                        { type: 'text', value: label },
                        ...(Object.entries(yearObj).flatMap(([key, value]) =>
                          ledgerViewYears.includes(Number(key))
                            ? {
                                type: 'text',
                                value:
                                  convertActivityAndResultTargetTableDataValue(
                                    value,
                                  ),
                                align: 'right',
                                ratio: 10,
                              }
                            : [],
                        ) as ReportTableCell[]),
                      ] satisfies ReportTableCell[],
                    };
                  },
                );

                return [
                  {
                    subtitle: getIndicatorSectionTitle(
                      routeIndicator.indicator.type,
                      routeIndicator.indicator.term,
                    ),
                    item: (
                      <div>
                        <Flex>
                          <ReportSectionItemContent
                            label={getIndicatorTitleLabel(
                              routeIndicator.indicator.type,
                            )}
                            value={routeIndicator.indicator.title}
                            wrapperStyle={{
                              height: 'auto',
                            }}
                          />
                          <ReportSectionItemContent
                            label={getIndicatorDescriptionLabel(
                              routeIndicator.indicator.type,
                            )}
                            value={routeIndicator.indicator.description}
                            wrapperStyle={{
                              height: 'auto',
                              borderLeft: LEDGER_BORDER,
                            }}
                          />
                        </Flex>
                        <Flex>
                          <ReportSectionItemContent
                            label={t('achievement-for-qualitative-outcome')}
                            value={
                              routeIndicator.indicator
                                .statusOfAchievementWhenQualitative
                            }
                            wrapperStyle={{
                              height: 'auto',
                            }}
                          />
                          <ReportSectionItemContent
                            label={t('indicator-setting-reason')}
                            value={routeIndicator.indicator.reason}
                            wrapperStyle={{
                              height: 'auto',
                              borderLeft: LEDGER_BORDER,
                            }}
                          />
                        </Flex>
                        <Flex>
                          <ReportSectionItemContent
                            label={t('reason-for-setting-qualitative-outcome')}
                            value={routeIndicator.indicator.noTypeReason}
                            wrapperStyle={{
                              height: 'auto',
                            }}
                            isLast
                          />
                          <ReportSectionItemContent
                            label={t('reason-why-outcomes-cannot-be-multiple')}
                            value={
                              routeIndicator.edge
                                ?.reasonWhyOutcomesCannotBeSetAtMultipleLevels
                            }
                            wrapperStyle={{
                              height: 'auto',
                              borderLeft: LEDGER_BORDER,
                            }}
                            isLast
                          />
                        </Flex>
                      </div>
                    ),
                  },
                  ...(routeIndicator.indicator.type === 'outcome' &&
                  routeIndicator.indicator.dataNatureType === 'qualitative'
                    ? []
                    : [
                        {
                          subtitle: t('activity-and-result-target'),
                          item: (
                            <ReportSectionItemContent
                              isTable
                              value={
                                <ReportSectionItemTable
                                  header={tableHeader}
                                  data={tableBody}
                                />
                              }
                            />
                          ),
                        },
                      ]),
                  ...(nextRouteIndicator
                    ? ([
                        {
                          isLongText: true,
                          subtitleWrapperStyle: {
                            background: 'white',
                            display: 'flex',
                          },
                          subtitle: (
                            <>
                              <ArrowDownImage type="raw" width={24} />
                              <Text
                                variant="footnote"
                                text={t('connection-to-next-outcome')}
                              />
                            </>
                          ),
                          item: (
                            <ReportSectionItemContent
                              wrapperStyle={{ borderBottom: 'none' }}
                              value={nextRouteIndicator.edge?.label}
                            />
                          ),
                        },
                      ] satisfies ReportSectionProps['items'])
                    : []),
                ];
              }),
              ...(isLast
                ? [
                    {
                      subtitle: t(
                        'cabinet-decisions-where-related-indicators-are-defined',
                      ),
                      item: (
                        <Flex
                          vertical
                          style={{ width: '100%', height: '100%' }}
                        >
                          <ReportSectionItemContent
                            label={t('name')}
                            value={
                              project?.cabinetDecisionWithDefinedIndicators
                                ?.name
                            }
                            wrapperStyle={{ borderBottom: LEDGER_BORDER }}
                          />
                          <ReportSectionItemContent
                            label={t('url')}
                            value={
                              project?.cabinetDecisionWithDefinedIndicators?.url
                            }
                            wrapperStyle={{ borderBottom: LEDGER_BORDER }}
                          />
                          <ReportSectionItemContent
                            label={t('relevant-part')}
                            value={
                              project?.cabinetDecisionWithDefinedIndicators
                                ?.relevantPart
                            }
                            isLast
                          />
                        </Flex>
                      ),
                    },
                  ]
                : []),
            ]}
          />
        );
      })}
    </>
  );
};
